<template>
  <div class="tasks-widget" :class="mode">
    <div class="header" v-if="isUnread">Task with unread notes</div>
    <slot>
      <div class="header flex items-center justify-between" v-if="!isUnread">
        <div class="flex items-center gap-1">
          <el-input
            placeholder="Keyword (enter at least 5 characters)"
            v-model="filter.keyword"
            size="mini"
            class="w250"
            clearable
            @change="searchKeyword"
          >
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
          <el-checkbox
            v-if="myStats.unread > 0"
            v-model="showingUnread"
            size="mini"
            border
            @change="onUnreadFilterChange"
          >
            <span class="red font-bold">Unread ({{ myStats.unread }})</span>
          </el-checkbox>
        </div>
        <a
          v-if="isPopup"
          class="fas fa-xmark fs18"
          @click="$emit('closeDialog')"
        />
      </div>
      <div class="main flex-start">
        <div class="stats flex-space-v" v-if="!isUnread">
          <div class="set-status">
            <el-input
              size="mini"
              v-model="status.details"
              placeholder="Set Status"
              clearable
              @change="setStatus"
            />
          </div>
          <div class="assignees space">
            <div class="title">Assignee Stats</div>
            <ul>
              <li
                class="flex-space align-center"
                @click="selectAssignee('all')"
              >
                <div><i class="fas fa-users gray" />All Teammates</div>
                <div>
                  <small class="brown">{{
                    sumArray(
                      members.map((o) => o.unread),
                      0,
                    )
                  }}</small>
                  <span>/</span>
                  <small class="red">
                    {{
                      sumArray(
                        members.map((o) => o.undone),
                        0,
                      )
                    }}
                  </small>
                </div>
              </li>
              <li
                v-for="member in members"
                :key="member.uuid"
                :class="{ selected: filter.user_ids.includes(member.id) }"
                @click="selectAssignee(member)"
              >
                <div class="flex-space align-center">
                  <div class="flex-start align-center space">
                    <el-avatar
                      size="small"
                      :class="{ [onlineStatus(member)]: true }"
                      >{{ member.abbrev }}
                    </el-avatar>
                    <div class="name">&nbsp;{{ member.name }}</div>
                  </div>
                  <div>
                    <small class="brown">{{ member.unread }}</small>
                    <span>/</span>
                    <small class="red">{{ member.undone }}</small>
                  </div>
                </div>
                <em class="at-status">{{ atStatus(member) }}</em>
              </li>
            </ul>
          </div>
        </div>
        <div class="tasks flex-space-v">
          <div class="search" v-if="!isUnread">
            <el-form
              label-position="top"
              size="mini"
              :disabled="!isEmptyData(filter.keyword)"
            >
              <el-row :gutter="2">
                <el-col :span="5">
                  <el-form-item label="">
                    <el-radio-group
                      v-model="filter.status"
                      class="block"
                      @change="loadTasks"
                    >
                      <el-radio-button label="all" class="pw15"
                        >All</el-radio-button
                      >
                      <el-radio-button label="pending" class="pw35"
                        >Pending</el-radio-button
                      >
                      <el-radio-button label="hold" class="pw25"
                        >Hold</el-radio-button
                      >
                      <el-radio-button label="done" class="pw25"
                        >Done</el-radio-button
                      >
                    </el-radio-group>
                  </el-form-item>
                </el-col>
                <el-col :span="7">
                  <el-form-item label="">
                    <el-radio-group
                      v-model="filter.group"
                      class="block"
                      @change="loadTasks"
                    >
                      <el-radio-button label="all" class="pw12"
                        >All</el-radio-button
                      >
                      <el-radio-button label="assigned_to_me" class="pw28"
                        >Assigned</el-radio-button
                      >
                      <el-radio-button label="following" class="pw32"
                        >Following</el-radio-button
                      >
                      <el-radio-button label="created" class="pw28"
                        >Created</el-radio-button
                      >
                    </el-radio-group>
                  </el-form-item>
                </el-col>
                <el-col :span="5">
                  <el-form-item label="">
                    <el-radio-group
                      v-model="filter.archived"
                      class="block"
                      @change="loadTasks"
                    >
                      <el-radio-button label="0" class="pw55"
                        >Unarchived</el-radio-button
                      >
                      <el-radio-button label="1" class="pw45"
                        >Archived</el-radio-button
                      >
                    </el-radio-group>
                  </el-form-item>
                </el-col>
                <el-col :span="7">
                  <el-form-item label="">
                    <el-select
                      v-model="filter.tags"
                      multiple
                      collapse-tags
                      placeholder="Tags"
                      class="block"
                      :clearable="false"
                      @change="loadTasks"
                      @focus="loadTags"
                    >
                      <el-option
                        v-for="item in tags"
                        :key="item.tag"
                        :label="item.tag"
                        :value="item.tag"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
          <div class="new-task" v-if="!isUnread">
            <el-input
              size="mini"
              v-model="task.title"
              placeholder="+ New Task"
              clearable
              @keypress.enter.native="createTask"
            />
            <div class="tip" v-if="!isEmptyData(task.title)">
              Hit 'Enter' to Create
            </div>
          </div>
          <div class="v-tasks space" v-if="!isEmptyData(tasks)">
            <Task
              v-for="i in tasks"
              :members="members"
              :user="user"
              :task="i"
              :key="i.uuid"
            />
          </div>
          <div
            class="v-task-actions flex-space align-center"
            v-if="selectedTasks.length > 0"
          >
            <div class="selected flex-space align-center">
              <div class="num">{{ selectedTasks.length }}</div>
              <div>Selected</div>
            </div>
            <div class="space" />
            <div class="flex-start align-center">
              <el-popconfirm
                title="Are you sure to archive the selected tasks?"
                confirmButtonText="Archive"
                cancelButtonText="Cancel"
                @confirm="archiveTasks"
                v-if="filter.archived == '0'"
              >
                <a class="icon-label" slot="reference">
                  <i class="fas fa-box-archive" />
                  <div>Archive</div>
                </a>
              </el-popconfirm>
              <el-popconfirm
                title="Are you sure to unarchive the selected tasks?"
                confirmButtonText="Unarchive"
                cancelButtonText="Cancel"
                @confirm="unarchiveTasks"
                v-else
              >
                <a class="icon-label" slot="reference">
                  <i class="fas fa-box-archive" />
                  <div>Unarchive</div>
                </a>
              </el-popconfirm>
              <el-popconfirm
                title="Are you sure to delete the selected tasks?"
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                @confirm="deleteTasks"
              >
                <a class="icon-label" slot="reference">
                  <i class="fas fa-trash" />
                  <div>Delete</div>
                </a>
              </el-popconfirm>
            </div>
            <div class="unselect" @click="unselectTasks">
              <i class="fas fa-xmark" />
            </div>
          </div>
        </div>
      </div>
    </slot>
  </div>
</template>
<script>
import api from "@/default/utils/api";
import Task from "@/default/pages/tasks/task.vue";
import { format } from "date-fns";
export default {
  name: "TaskWidget",
  mixins: [api],
  components: {
    Task,
  },
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
    },
  },
  data() {
    return {
      loaded: false,
      timestamp: null,
      status: {},
      intervalId: null,
      filter: {
        keyword: "",
        status: "all",
        group: "all",
        archived: "0",
        user_ids: [],
        hub_id: "",
        tags: [],
      },
      task: {},
      tasks: [],
      cachedTasks: [],
      members: [],
      tags: [],
      keywordFocus: false,
      notifications: {},
      showingUnread: false,
      format,
    };
  },
  async mounted() {
    await this.loadStats();
    if (this.visible) {
      if (this.mode != "unread") await this.widgetOpened();
      if (this.mode == "unread") await this.loadUnread();
    }
    window.TaskWidget = { receive: this.receive };
    window.addEventListener(this.taskNoteEventName, this.receivedNote);
  },
  beforeDestroy() {
    window.TaskWidget = null;
    window.removeEventListener(this.taskNoteEventName, this.receivedNote);
    window.clearInterval(this.intervalId);
  },
  watch: {
    myStatus(nv, ov) {
      this.$set(this.status, "details", nv.details);
    },
    async visible(nv, ov) {
      if (nv) await this.widgetOpened();
    },
  },
  computed: {
    isPopup() {
      return this.mode == "popup";
    },
    isUnread() {
      return this.mode == "unread";
    },
    taskNoteEventName() {
      return `task-note-at-${this.user.id}`;
    },
    selectedTasks() {
      return this.tasks.filter((o) => o.selected);
    },
    myStats() {
      return (
        this.members.find((o) => o.id == this.user.id) || {
          unread: 0,
          undone: 0,
        }
      );
    },
    onlineStatus() {
      return function (user) {
        const time = (this.$store.users[user.uuid] || {})["online"];
        if (!time) return "offline";
        const diff = this.timestamp - time;
        if (diff < 1000 * 30) return "online";
        if (diff < 1000 * 60 * 5) return "away";
        else return "offline";
      };
    },
    myStatus() {
      return (this.$store.users[this.user.uuid] || {})["status"] || {};
    },
    atStatus() {
      return function (user) {
        const { at, details } =
          (this.$store.users[user.uuid] || {})["status"] || {};
        return at ? `${at}: ${details}` : null;
      };
    },
  },
  methods: {
    intervalClock() {
      this.intervalId = window.setInterval(
        () => (this.timestamp = new Date().getTime()),
        10000,
      );
    },
    async loadStats() {
      try {
        const members = window.TaskPopup?.loadStats
          ? await window.TaskPopup.loadStats()
          : (await this.$http.get("/api/v1/tasks/stats")).data;
        if (members?.length) this.members = members;
        if (!window.TaskPopup?.loadStats) this.updateSidebarTaskStats();
      } catch (ex) {}
    },
    updateSidebarTaskStats() {
      const el = document.querySelector("#sidebar-task-stats");
      if (!el || !this.user || !this.user.id) return;
      const member = this.members.find((m) => m.id === this.user.id);
      const unread = member ? member.unread : 0;
      const undone = member ? member.undone : 0;
      const unreadEl = el.querySelector(".unread");
      const undoneEl = el.querySelector(".undone");
      if (unreadEl) unreadEl.textContent = unread;
      if (undoneEl) undoneEl.textContent = undone;
    },
    async widgetOpened() {
      if (!this.loaded) {
        this.timestamp = new Date().getTime();
        this.intervalClock();
        this.filter.user_ids = [this.user.id];
        this.loaded = true;
        await this.loadTags();
        await this.loadTasks();
      }
    },
    async setStatus() {
      try {
        this.status.at = this.format(new Date(), "HH:mm");
        if (this.isEmptyData(this.status.details)) {
          await this.$http.put("/api/v1/interactors/0", {
            interactor: { status: {} },
          });
        } else {
          await this.$http.put("/api/v1/interactors/0", {
            interactor: { status: this.status },
          });
        }
      } catch (ex) {
        console.log(ex);
      }
    },
    async loadUnread() {
      try {
        this.showingUnread = true;
        this.tasks = (await this.$http.get("/api/v1/tasks/unread")).data;
      } catch (ex) {}
    },
    async onUnreadFilterChange(checked) {
      if (checked) await this.loadUnread();
      else await this.loadTasks();
    },
    async selectAssignee(member) {
      this.filter.status = "all";
      this.filter.group = "all";
      this.filter.archived = "0";
      if (member == "all") {
        this.filter.user_ids = this.members.map((o) => o.id);
      } else {
        this.filter.user_ids = [member.id];
      }
      await this.loadTasks();
    },
    async loadTags() {
      try {
        this.tags = (await this.$http.get("/api/v1/tasks/tags")).data;
      } catch (ex) {}
    },
    async loadTasks() {
      try {
        this.showingUnread = false;
        this.tasks = [];
        this.tasks = (
          await this.$http.get("/api/v1/tasks", { params: this.filter })
        ).data;
      } catch (ex) {}
    },
    async createTask() {
      try {
        if (this.isEmptyData(this.task.title)) return;
        const task = { ...this.task };
        if (!task.related) {
          delete task.taskable_type;
          delete task.taskable_id;
        }
        const response = await this.$http.post("/api/v1/tasks", { task });
        this.tasks.unshift(response.data);
        this.task.title = null;
        this.$message({ message: "New task was created", type: "success" });
        await this.loadStats();
      } catch (ex) {}
    },
    unselectTasks() {
      this.selectedTasks.forEach((o) => (o.selected = false));
    },
    async archiveTasks() {
      try {
        const ids = this.selectedTasks.map((o) => o.id);
        const params = { ids, target: "archive" };
        await this.$http.post("/api/v1/tasks/batch", params);
        this.selectedTasks.forEach((o) => {
          o.archived = true;
          o.selected = false;
        });
        await this.loadStats();
      } catch (ex) {}
    },
    async unarchiveTasks() {
      try {
        const ids = this.selectedTasks.map((o) => o.id);
        const params = { ids, target: "unarchive" };
        await this.$http.post("/api/v1/tasks/batch", params);
        this.selectedTasks.forEach((o) => {
          o.archived = false;
          o.selected = false;
        });
        await this.loadStats();
      } catch (ex) {}
    },
    async deleteTasks() {
      try {
        const ids = this.selectedTasks.map((o) => o.id);
        const params = { ids, target: "delete" };
        await this.$http.post("/api/v1/tasks/batch", params);
        this.selectedTasks.forEach((st) => {
          let index = this.tasks.findIndex((o) => o.id == st.id);
          if (index >= 0) this.tasks.splice(index, 1);
          index = this.cachedTasks.findIndex((o) => o.id == st.id);
          if (index >= 0) this.cachedTasks.splice(index, 1);
        });
        await this.loadStats();
      } catch (ex) {}
    },
    highlightLabel(name, value) {
      return this[name] == value ? "active" : "";
    },
    async searchKeyword() {
      try {
        this.filter.keyword = this.filter.keyword.trim();
        const length = this.filter.keyword.length;
        if (length == 0) {
          await this.loadTasks();
          return;
        }
        if (length >= 5) {
          await this.loadTasks();
        } else {
          this.$message({
            message: "Please enter at least 5 characters to search",
            type: "warning",
          });
        }
      } catch (ex) {}
    },
    withTask(id) {
      return this.tasks.find((o) => o.id == id) != null;
    },
    async receive(id) {
      try {
        const task = this.tasks.find((o) => o.id == id);
        if (task) {
          const { notes } = (await this.$http.get(`/api/v1/tasks/${id}`)).data;
          this.$set(task, "notes", notes);
        }
      } catch (ex) {}
    },
    async receivedNote(event) {
      try {
        if (event.type == this.taskNoteEventName) {
          await this.loadStats();
          const detail = event.detail;
          if (detail.action == "unread") {
            await this.receive(detail.noteable_id);
          }
        }
      } catch (ex) {}
    },
  },
};
</script>
<style lang="scss">
.tasks-widget {
  box-shadow: rgba(0, 0, 0, 0.3) 0 0 6px, rgba(0, 0, 0, 0.15) 0 1px 6px;
  z-index: 99 !important;
  height: 100%;

  .header {
    background: #f2f6fc;
    padding: 3px;
    border-bottom: 1px solid #ddd;
  }

  .unread-count {
    font-size: 10px;
    left: -6px;
    color: red;
  }

  .stats {
    min-width: 160px;
    max-width: 160px;
    background: #fff;
    border-right: 1px solid #eee;
    padding: 2px;
    font-size: 12px;

    .assignees {
      @apply px-0.5;
      overflow: auto;

      .title {
        font-weight: bold;
        padding: 5px 2px;
      }

      li {
        margin: 1px 0;
        padding: 3px 2px;
        cursor: pointer;
        position: relative;

        &.selected {
          background: #e1eaff;
        }

        .name {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          font-size: 11px;
        }

        .at-status {
          font-size: 9px;
          color: #666;
          position: absolute;
          bottom: -5px;
          left: 24px;
        }
      }

      .el-avatar {
        font-family: Arial, Verdana, sans-serif;
        flex-shrink: 0;
        font-size: 10px;
        font-weight: bold;

        &.el-avatar--small {
          width: 20px;
          height: 20px;
          line-height: 20px;
        }

        &.online {
          background: green;
        }

        &.away {
          background: orange;
        }
      }
    }

    .set-status {
      margin: -2px;
      padding: 3px;
      background: #e0e0e0;
      border-bottom: 1px solid #ddd;
    }
  }

  .main {
    .tasks {
      width: 100%;
      overflow: auto;
    }
  }

  .search {
    flex-grow: 0;
    padding: 4px;
    border-bottom: 1px solid #ddd;

    .el-form-item {
      margin-bottom: 0;
    }

    .el-radio-button--mini {
      .el-radio-button__inner {
        padding: 7px 2px;
        font-size: 11px;
        width: 100%;
      }
    }
  }

  .new-task {
    background: #dddddd;
    padding: 4px;
    position: relative;

    .tip {
      position: absolute;
      right: 18px;
      top: 16.5px;
      font-size: 10px;
      font-style: italic;
      color: #999;
    }
  }
}

.v-tasks {
  padding: 3px;
  overflow: auto;
}

.v-task-actions {
  background: #fff;
  margin: 2px;
  padding: 0;
  border: 1px solid #eee;
  border-radius: 3px;
  box-sizing: border-box;

  .selected {
    font-size: 12px;
    line-height: 40px;

    .num {
      background: #409eff;
      color: #fff;
      padding: 0px 8px;
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
      font-weight: bold;
      font-size: 20px;
      margin-right: 5px;
    }
  }

  .unselect {
    font-size: 20px;
    padding: 0 5px;
    background: red;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    color: #fff;
    cursor: pointer;
    margin-left: 10px;
    line-height: 40px;
  }

  a.icon-label {
    display: block;
    font-size: 10px;
    text-align: center;
    margin: 0 10px;
    color: #666;

    i.fas {
      font-size: 18px;
    }

    div {
      line-height: 12px;
    }
  }
}

.task-modal {
  .new-task {
    position: relative;

    input.el-input__inner {
      padding: 0 10px !important;
    }

    .tip {
      position: absolute;
      right: 20px;
      top: 20px;
      font-size: 12px;
      font-style: italic;
      color: #999;
    }
  }

  .v-tasks {
    margin-top: 10px;
    max-height: 450px;
    overflow: auto;
    border: 1px solid #ddd;
    padding: 0 3px;
  }
}
</style>
